import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { apiService } from '../../../components/Config/api.service';
import LoadingBarWithStyles from '../../../components/Utils/LoadingBarWithStyles';
import { notify } from '../../../components/Utils/Notifications';

/**
 * « Accès et droits » (YRH-CORE-013, contrat C27) : corriger ou ajouter un
 * pointage change les heures de la feuille de temps, donc la paie. L'admin
 * désigne, parmi les comptes RH de sa société, ceux qui ont ce droit.
 *
 * Le portail n'invente rien. Il affiche ce que le serveur rend — les comptes
 * gestionnaires RH de la société active, triés par nom, admins et superadmins
 * compris — et écrit le droit compte par compte. Un compte qui l'a d'office
 * (admin, superadmin) est coché et inactif : ce droit ne se retire pas. La
 * ligne ne bascule qu'avec la réponse du serveur.
 */

// L'enveloppe C27 d'un appel : celle du serveur, ou une enveloppe vide — ni
// `ok`, ni `message` — quand il n'y en a pas. Le statut d'abord, jamais la
// présence d'un message : un 502 porte lui aussi un corps, mais dans `error`,
// jamais un refus métier.
async function enveloppeDe(appel) {
  try {
    return (await appel()) || {};
  } catch (erreur) {
    return erreur?.response?.status === 409 ? erreur.response.data || {} : {};
  }
}

// Le `message` du serveur d'abord, mot pour mot ; le repli i18n par `motif`
// ensuite ; le message générique pour un motif hors contrat.
const REPLI_PAR_MOTIF = {
  non_autorise: 'accesDroits.refus.non_autorise',
  valeurs_invalides: 'accesDroits.refus.valeurs_invalides',
  utilisateur_inconnu: 'accesDroits.refus.utilisateur_inconnu',
  droit_d_office: 'accesDroits.refus.droit_d_office',
};
const messageDe = (enveloppe, t, cleGenerique) =>
  enveloppe.message || t(REPLI_PAR_MOTIF[enveloppe.motif] || cleGenerique);

function AccesDroits() {
  const { t } = useTranslation();
  const [comptes, setComptes] = useState(null);
  const [echec, setEchec] = useState(null);
  // Les comptes dont l'écriture est partie sans être revenue : leur
  // interrupteur est inactif, la demande ne part pas deux fois.
  const [enCours, setEnCours] = useState([]);

  useEffect(() => {
    (async () => {
      const lecture = await enveloppeDe(() => apiService.lireDroitsPointages());
      if (lecture.ok && Array.isArray(lecture.comptes)) setComptes(lecture.comptes);
      else setEchec(lecture);
    })();
  }, []);

  const changer = async (compte) => {
    // Un droit d'office ne se change pas, et une écriture partie ne se double
    // pas : la garde est ici, pas seulement dans l'attribut `disabled`, qu'un
    // événement synthétique contourne.
    if (compte.d_office || enCours.includes(compte.user_id)) return;
    setEnCours((courants) => [...courants, compte.user_id]);
    const ecriture = await enveloppeDe(() =>
      apiService.ecrireDroitPointage({
        user_id: compte.user_id,
        corriger_pointages: !compte.corriger_pointages,
      })
    );
    setEnCours((courants) => courants.filter((id) => id !== compte.user_id));
    if (!ecriture.ok || !ecriture.compte) {
      notify(messageDe(ecriture, t, 'accesDroits.erreurGenerique'), '', 3);
      return;
    }
    // La ligne prend la réponse du serveur, à sa place dans la liste.
    setComptes((courants) =>
      courants.map((courant) => (courant.user_id === ecriture.compte.user_id ? ecriture.compte : courant))
    );
  };

  if (echec) {
    return (
      <p
        data-testid="acces-erreur"
        className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
      >
        {messageDe(echec, t, 'accesDroits.erreurChargement')}
      </p>
    );
  }
  if (!comptes) return <LoadingBarWithStyles />;

  const etat = (compte) => {
    if (compte.d_office) return t('accesDroits.dOffice');
    return compte.corriger_pointages ? t('accesDroits.peutCorriger') : t('accesDroits.lectureSeule');
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <p data-testid="acces-aide" className="text-sm text-slate-600">{t('accesDroits.aide')}</p>
        <p data-testid="acces-delai" className="text-sm text-slate-500">{t('accesDroits.delai')}</p>
      </div>

      {comptes.length === 0 ? (
        <p data-testid="acces-vide" className="text-sm text-slate-400">{t('accesDroits.vide')}</p>
      ) : (
        <ul
          data-testid="acces-liste"
          className="bg-white rounded-2xl shadow-sm border border-slate-100 divide-y divide-slate-100"
        >
          {comptes.map((compte) => (
            <li
              key={compte.user_id}
              data-testid={`compte-${compte.user_id}`}
              className="flex items-center gap-4 py-3 px-4"
            >
              <p className="flex-1 min-w-0 text-sm font-medium text-slate-800 truncate">{compte.nom}</p>
              <span className="text-sm text-slate-600">{etat(compte)}</span>
              {/* Contrôlée : la case ne bascule qu'avec la réponse du serveur. */}
              <input
                type="checkbox"
                role="switch"
                className="toggle toggle-sm toggle-success"
                checked={compte.corriger_pointages === true}
                disabled={compte.d_office === true || enCours.includes(compte.user_id)}
                onChange={() => changer(compte)}
                aria-label={t('accesDroits.interrupteur', { nom: compte.nom })}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default AccesDroits;
